import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SpecialtyProvider } from './contexts/SpecialtyContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SpecialtySelection from './pages/SpecialtySelection';
import Fatture from './pages/Fatture';
import ClientiPage from './pages/Clienti';
import Referti from './pages/Referti';
import DynamicNuovoReferto from './components/DynamicNuovoReferto';
import NuovaFattura from './pages/NuovaFattura';
import Settings from './pages/Settings';
import apiService from './services/api';

// Componente per le route protette
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode;
  requiresSpecialty?: boolean;
}> = ({ children, requiresSpecialty = true }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="text-center p-4">Caricamento...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout requiresSpecialty={requiresSpecialty}>{children}</Layout>;
};

// Componente per route che non richiedono specialistica (come la selezione)
const AuthOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="text-center p-4">Caricamento...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Componente che configura il callback per l'API service
const ApiCallbackConfigurator: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Configura il callback per gestire gli errori di autenticazione
    apiService.setAuthErrorCallback(() => {
      console.log('🔄 [API] Callback di auth error chiamato, navigazione a /login');
      navigate('/login', { replace: true });
    });
  }, [navigate]);
  
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <SpecialtyProvider>
        <Router>
          <ApiCallbackConfigurator>
            <div className="App">
            <Routes>
              <Route path="/login" element={<Login />} />
              
              {/* Route di selezione specialistica - solo autenticazione */}
              <Route
                path="/specialty-selection"
                element={
                  <AuthOnlyRoute>
                    <SpecialtySelection />
                  </AuthOnlyRoute>
                }
              />

              {/* Dashboard - reindirizza alla selezione se manca specialistica */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/"
                element={<Navigate to="/dashboard" replace />}
              />
              
              {/* Route fatturazione - con parametro specialistica */}
              <Route
                path="/fatture/:specialtyId"
                element={
                  <ProtectedRoute>
                    <Fatture />
                  </ProtectedRoute>
                }
              />
              
              {/* Route nuova fattura */}
              <Route
                path="/fatture/nuova"
                element={
                  <ProtectedRoute>
                    <NuovaFattura />
                  </ProtectedRoute>
                }
              />
              
              {/* Route storico fatture - con parametro specialistica */}
              <Route
                path="/fatture/:specialtyId/storico"
                element={
                  <ProtectedRoute>
                    <Fatture />
                  </ProtectedRoute>
                }
              />
              
              {/* Route clienti - sempre disponibili */}
              <Route
                path="/clienti"
                element={
                  <ProtectedRoute>
                    <ClientiPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Route referti - con parametro specialistica */}
              <Route
                path="/referti/:specialtyId"
                element={
                  <ProtectedRoute>
                    <Referti />
                  </ProtectedRoute>
                }
              />
              
              {/* Route bozze referti - con parametro specialistica */}
              <Route
                path="/referti/:specialtyId/bozze"
                element={
                  <ProtectedRoute>
                    <Referti />
                  </ProtectedRoute>
                }
              />
              
              {/* Route specifiche per specialistica - dinamiche */}
              <Route
                path="/referti/:specialtyName/nuovo"
                element={
                  <ProtectedRoute>
                    <DynamicNuovoReferto />
                  </ProtectedRoute>
                }
              />
              
              {/* Route settings */}
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              
              {/* Fallback */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
            </div>
          </ApiCallbackConfigurator>
        </Router>
      </SpecialtyProvider>
    </AuthProvider>
  );
};

export default App;
